/**
 * FAQ Service
 * Handles FAQ management API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { FAQ_ENDPOINTS, buildQueryString } from '../endpoints';
import DOMPurify from 'dompurify';

/**
 * @typedef {Object} CreateFaqDto
 * @property {string} question - FAQ question (max 500 chars, required)
 * @property {string} answer - FAQ answer (required)
 * @property {string} [category] - FAQ category (max 100 chars)
 * @property {number} [displayOrder] - Display order (default: 0)
 * @property {boolean} [isActive] - Whether FAQ is active (default: true)
 * @property {boolean} [isPublic] - Whether FAQ is public (default: true)
 * @property {number} [organizationId] - Organization ID (null for global FAQs)
 */

/**
 * @typedef {Object} UpdateFaqDto
 * @property {string} [question] - FAQ question (max 500 chars)
 * @property {string} [answer] - FAQ answer
 * @property {string} [category] - FAQ category (max 100 chars)
 * @property {number} [displayOrder] - Display order
 * @property {boolean} [isActive] - Whether FAQ is active
 * @property {boolean} [isPublic] - Whether FAQ is public
 */

/**
 * @typedef {Object} FaqListParams
 * @property {boolean} [includeInactive] - Include inactive FAQs (default: false)
 * @property {number} [organizationId] - Filter by organization ID
 */

/**
 * Strip HTML tags and return plain text content
 * Handles TinyMCE empty states like <p><br></p>
 */
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = DOMPurify.sanitize(html);
  return (tmp.textContent || tmp.innerText || '').trim();
};

const faqService = {
  /**
   * Get public FAQs (no authentication required)
   * @param {number} [organizationId] - Optional organization ID filter
   * @returns {Promise<Array>} List of public FAQs
   */
  getPublicFaqs: async (organizationId = null) => {
    const queryString = organizationId ? buildQueryString({ organizationId }) : '';
    return apiRequest(() =>
      axiosInstance.get(`${FAQ_ENDPOINTS.PUBLIC}${queryString}`)
    );
  },

  /**
   * Get public FAQs by category (no authentication required)
   * @param {string} category - FAQ category
   * @param {number} [organizationId] - Optional organization ID filter
   * @returns {Promise<Array>} List of public FAQs in category
   */
  getPublicFaqsByCategory: async (category, organizationId = null) => {
    const queryString = organizationId ? buildQueryString({ organizationId }) : '';
    return apiRequest(() =>
      axiosInstance.get(`${FAQ_ENDPOINTS.PUBLIC_BY_CATEGORY(category)}${queryString}`)
    );
  },

  /**
   * Get all FAQs (admin only)
   * @param {FaqListParams} [params] - Query parameters
   * @returns {Promise<Array>} List of FAQs
   */
  getAllFaqs: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${FAQ_ENDPOINTS.LIST}${queryString}`)
    );
  },

  /**
   * Get FAQ by ID
   * @param {number} id - FAQ ID
   * @returns {Promise<Object>} FAQ data
   */
  getFaqById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(FAQ_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Create new FAQ (admin only)
   * @param {CreateFaqDto} faqData - FAQ data
   * @returns {Promise<Object>} Created FAQ
   */
  createFaq: async (faqData) => {
    return apiRequest(() =>
      axiosInstance.post(FAQ_ENDPOINTS.CREATE, faqData)
    );
  },

  /**
   * Update existing FAQ (admin only)
   * @param {number} id - FAQ ID
   * @param {UpdateFaqDto} faqData - Updated FAQ data
   * @returns {Promise<Object>} Updated FAQ
   */
  updateFaq: async (id, faqData) => {
    return apiRequest(() =>
      axiosInstance.put(FAQ_ENDPOINTS.UPDATE(id), faqData)
    );
  },

  /**
   * Delete FAQ (admin only)
   * @param {number} id - FAQ ID
   * @returns {Promise<void>}
   */
  deleteFaq: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(FAQ_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Reorder FAQs (admin only)
   * @param {Object} faqOrderMap - Map of FAQ ID to display order
   * @returns {Promise<Object>} Success message
   */
  reorderFaqs: async (faqOrderMap) => {
    return apiRequest(() =>
      axiosInstance.post(FAQ_ENDPOINTS.REORDER, faqOrderMap)
    );
  },

  generateAnswer: async (question, context = null) => {
    return apiRequest(() =>
      axiosInstance.post(FAQ_ENDPOINTS.GENERATE_ANSWER, { question, context }, { timeout: 30000 })
    );
  },

  polishAnswer: async (question, answer) => {
    return apiRequest(() =>
      axiosInstance.post(FAQ_ENDPOINTS.POLISH_ANSWER, { question, answer }, { timeout: 30000 })
    );
  },

  /**
   * Helper: Validate FAQ creation data
   * @param {CreateFaqDto} faqData - FAQ data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateCreateData: (faqData) => {
    const errors = [];

    // Required fields
    if (!faqData.question || faqData.question.trim() === '') {
      errors.push('Question is required');
    }

    if (!faqData.answer || stripHtml(faqData.answer) === '') {
      errors.push('Answer is required');
    }

    // Length validations
    if (faqData.question && faqData.question.length > 500) {
      errors.push('Question must not exceed 500 characters');
    }

    if (faqData.category && faqData.category.length > 100) {
      errors.push('Category must not exceed 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Validate FAQ update data
   * @param {UpdateFaqDto} faqData - FAQ data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateUpdateData: (faqData) => {
    const errors = [];

    // Length validations
    if (faqData.question && faqData.question.length > 500) {
      errors.push('Question must not exceed 500 characters');
    }

    if (faqData.category && faqData.category.length > 100) {
      errors.push('Category must not exceed 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Group FAQs by category
   * @param {Array} faqs - Array of FAQ objects
   * @returns {Object} FAQs grouped by category
   */
  groupByCategory: (faqs) => {
    const grouped = {};
    const groupIcons = {};

    faqs.forEach(faq => {
      const category = faq.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(faq);
      // Track first non-null categoryIcon per group
      if (!groupIcons[category] && faq.categoryIcon) {
        groupIcons[category] = faq.categoryIcon;
      }
    });

    // Sort categories alphabetically, with 'General' first
    const sortedGrouped = {};
    const categories = Object.keys(grouped).sort();

    if (categories.includes('General')) {
      sortedGrouped['General'] = grouped['General'];
    }

    categories.forEach(category => {
      if (category !== 'General') {
        sortedGrouped[category] = grouped[category];
      }
    });

    // Attach icon info to result
    sortedGrouped._categoryIcons = groupIcons;

    return sortedGrouped;
  },

  /**
   * Helper: Format FAQ for display
   * @param {Object} faq - FAQ object
   * @returns {Object} Formatted FAQ
   */
  formatForDisplay: (faq) => {
    return {
      ...faq,
      categoryLabel: faq.category || 'General',
      statusLabel: faq.isActive ? 'Active' : 'Inactive',
      visibilityLabel: faq.isPublic ? 'Public' : 'Private',
      createdDate: faq.createdAt ? new Date(faq.createdAt).toLocaleDateString() : 'N/A',
    };
  },
};

export default faqService;
